"use client";

import { useCallback, useRef, useState } from "react";
import { useAction, useMutation, type ReactMutation } from "convex/react";
import type { IntakeEvidenceInput } from "@convex/schemas/validators/intakeEvidence";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { handleError } from "@/lib/handle-error";
import { useGuestUploadToken } from "@/components/product/use-guest-upload-token";
import type { Photo } from "@/lib/products/types";
import type { TruckCapacities } from "@convex/schemas/validators/truckCapacities";
import { track } from "@/lib/analytics/tracker";

const PHOTO_ATTACHMENT_ERROR =
  "Some photos could not be attached. Go back and upload them again.";

export interface VendorDraftInput {
  intent: "sell" | "recycle";
  productTypeSlug: string;
  quantityAvailable: number;
  minOrderQuantity?: number;
  truckCapacities?: TruckCapacities;
  priceCents?: number;
  condition?: string;
  sellerNotes?: string;
  intakeEvidence?: IntakeEvidenceInput;
  location: {
    line1?: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
  };
  photos: Photo[];
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
}

function trackContactFailure(
  input: VendorDraftInput,
  extras: {
    productId?: Id<"products">;
    reasonCode:
      "draft_validation" | "photo_attachment" | "email_delivery" | "unexpected";
    errorCount: number;
  },
) {
  track("product_upload.stage_failed", {
    mode: "public",
    stage: "contact",
    productTypeSlug: input.productTypeSlug,
    intent: input.intent,
    ...extras,
  });
}

function normalizeDraftFields(input: VendorDraftInput) {
  const location = {
    line1: input.location.line1?.trim() || undefined,
    line2: input.location.line2?.trim() || undefined,
    city: input.location.city.trim(),
    state: input.location.state.trim(),
    postalCode: input.location.postalCode.trim(),
  };
  const photos = input.photos.map((photo) => ({
    storageId: photo.storageId,
    slot: photo.slot,
  }));
  return {
    quantityAvailable: input.quantityAvailable,
    minOrderQuantity: input.minOrderQuantity,
    truckCapacities: input.truckCapacities,
    location,
    photos,
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email: input.email.trim(),
    phone: input.phone?.trim() || undefined,
    companyName: input.companyName?.trim() || undefined,
  };
}

type ContactFields = ReturnType<typeof contactFieldsOf>;

type PendingDraft = {
  productId: Id<"products">;
  sessionToken: string;
  productTypeSlug: string;
  sentContact: ContactFields | null;
  attemptedContact: ContactFields | null;
};

const LOCKED_CONTACT_ERROR =
  "We already emailed the link. Open it to change your name or company, or change the email to get a new link.";

function sameContact(left: ContactFields, right: ContactFields): boolean {
  return (
    left.firstName === right.firstName &&
    left.lastName === right.lastName &&
    left.email === right.email &&
    left.phone === right.phone &&
    left.companyName === right.companyName
  );
}

export interface SentDraft {
  productId: Id<"products">;
  sessionToken: string;
  email: string;
}

type UpdateDraftMutation = ReactMutation<
  typeof api.products.mutations.publicDraft.updateGuestListingDraft
>;

type CreateDraftMutation = ReactMutation<
  typeof api.products.mutations.publicDraft.createGuestListingDraft
>;

type SyncContext = Parameters<typeof syncExistingDraft>[2];

function contactFieldsOf(draftFields: ReturnType<typeof normalizeDraftFields>) {
  const { firstName, lastName, email, phone, companyName } = draftFields;
  return { firstName, lastName, email, phone, companyName };
}

async function syncExistingDraft(
  updateDraft: UpdateDraftMutation,
  pending: PendingDraft,
  context: {
    draftFields: ReturnType<typeof normalizeDraftFields>;
    input: VendorDraftInput;
    setErrors: (errors: string[]) => void;
    onTokenRotated: (sessionToken: string, email: string) => void;
    onLockAdopted: (draft: SentDraft) => void;
  },
): Promise<boolean> {
  const { productId, sessionToken, sentContact } = pending;
  const contact = contactFieldsOf(context.draftFields);
  const result = await updateDraft({
    productId,
    sessionToken,
    ...(sentContact ? contact : context.draftFields),
  });
  const locked = !("success" in result) && result.code === "DRAFT_LOCKED";
  if (locked && sentContact === null && pending.attemptedContact) {
    pending.sentContact = pending.attemptedContact;
    context.onLockAdopted({ productId, sessionToken, email: pending.attemptedContact.email });
    return await syncExistingDraft(updateDraft, pending, context);
  }
  const lockedWithChanges =
    locked && sentContact !== null && !sameContact(contact, sentContact);
  if (lockedWithChanges) {
    context.setErrors([LOCKED_CONTACT_ERROR]);
    return false;
  }
  if ("success" in result && sentContact) pending.sentContact = contact;
  if ("success" in result && result.sessionToken) {
    pending.sessionToken = result.sessionToken;
    context.onTokenRotated(result.sessionToken, contact.email);
  }
  return !reportSyncFailure(result, context, productId);
}

function reportSyncFailure(
  result: Awaited<ReturnType<UpdateDraftMutation>>,
  context: { input: VendorDraftInput; setErrors: (errors: string[]) => void },
  productId: Id<"products">,
): boolean {
  if (!("success" in result) && result.code !== "DRAFT_LOCKED") {
    context.setErrors([result.error]);
    trackContactFailure(context.input, {
      productId,
      reasonCode: "draft_validation",
      errorCount: 1,
    });
    return true;
  }
  if ("success" in result && result.failedPhotos?.length) {
    context.setErrors([PHOTO_ATTACHMENT_ERROR]);
    trackContactFailure(context.input, {
      productId,
      reasonCode: "photo_attachment",
      errorCount: result.failedPhotos.length,
    });
    return true;
  }
  return false;
}

async function startDraft(
  mutations: {
    createDraft: CreateDraftMutation;
    updateDraft: UpdateDraftMutation;
    uploadToken: string;
  },
  context: SyncContext,
): Promise<{ pending: PendingDraft | null; ready: boolean }> {
  const { input, draftFields, setErrors } = context;
  const result = await mutations.createDraft({
    uploadToken: mutations.uploadToken,
    intent: input.intent,
    productTypeSlug: input.productTypeSlug,
    priceCents: input.priceCents,
    condition: input.condition,
    sellerNotes: input.sellerNotes,
    intakeEvidence: input.intakeEvidence,
    ...draftFields,
  });
  if (!result.success) {
    setErrors(result.errors);
    trackContactFailure(input, {
      reasonCode: "draft_validation",
      errorCount: result.errors.length,
    });
    return { pending: null, ready: false };
  }
  const pending: PendingDraft = {
    productId: result.productId,
    sessionToken: result.sessionToken,
    productTypeSlug: input.productTypeSlug,
    sentContact: null,
    attemptedContact: null,
  };
  if (result.resumed) {
    const ready = await syncExistingDraft(mutations.updateDraft, pending, context);
    return { pending, ready };
  }
  if (result.failedPhotos?.length) {
    setErrors([PHOTO_ATTACHMENT_ERROR]);
    trackContactFailure(input, {
      productId: result.productId,
      reasonCode: "photo_attachment",
      errorCount: result.failedPhotos.length,
    });
    return { pending, ready: false };
  }
  return { pending, ready: true };
}

export function useVendorDraftFlow() {
  const { attestUpload, ensureFreshToken, generateUploadUrl, invalidateToken } =
    useGuestUploadToken();
  const createDraft = useMutation(
    api.products.mutations.publicDraft.createGuestListingDraft,
  );
  const updateDraft = useMutation(
    api.products.mutations.publicDraft.updateGuestListingDraft,
  );
  const sendMagicLink = useAction(api.auth.magicLink.sendVendorMagicLink);
  const pendingDraft = useRef<PendingDraft | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [sentDraft, setSentDraft] = useState<SentDraft | null>(null);

  const submit = useCallback(
    async (input: VendorDraftInput): Promise<boolean> => {
      setIsSubmitting(true);
      setErrors([]);
      try {
        const draftFields = normalizeDraftFields(input);
        if (pendingDraft.current?.productTypeSlug !== input.productTypeSlug)
          pendingDraft.current = null;
        const syncContext: SyncContext = {
          draftFields,
          input,
          setErrors,
          onTokenRotated: (sessionToken, email) =>
            setSentDraft((current) => current && { ...current, sessionToken, email }),
          onLockAdopted: setSentDraft,
        };
        if (!pendingDraft.current) {
          const started = await startDraft(
            { createDraft, updateDraft, uploadToken: await ensureFreshToken() },
            syncContext,
          );
          pendingDraft.current = started.pending;
          if (started.pending) invalidateToken();
          if (!pendingDraft.current || !started.ready) return false;
        } else if (
          !(await syncExistingDraft(updateDraft, pendingDraft.current, syncContext))
        ) {
          return false;
        }
        const { productId, sessionToken } = pendingDraft.current;
        pendingDraft.current.attemptedContact = contactFieldsOf(draftFields);
        const send = await sendMagicLink({ productId, sessionToken });
        if (!send.sent) {
          setErrors([
            "We couldn't send your confirmation email. Please try again.",
          ]);
          trackContactFailure(input, {
            productId,
            reasonCode: "email_delivery",
            errorCount: 1,
          });
          return false;
        }
        pendingDraft.current.sentContact = contactFieldsOf(draftFields);
        setSentDraft({
          productId,
          sessionToken: pendingDraft.current.sessionToken,
          email: draftFields.email,
        });
        return true;
      } catch (err) {
        const handled = handleError(err, { display: "silent" });
        if (handled.action === "ignore") return false;
        setErrors([
          handled.message,
        ]);
        trackContactFailure(input, {
          productId: pendingDraft.current?.productId,
          reasonCode: "unexpected",
          errorCount: 1,
        });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      ensureFreshToken,
      invalidateToken,
      createDraft,
      updateDraft,
      sendMagicLink,
    ],
  );

  return {
    attestUpload,
    submit,
    isSubmitting,
    errors,
    setErrors,
    sentDraft,
    generateUploadUrl,
  };
}
